import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import SectionContact from '../components/SectionContact';
import Bande from '../components/Bande';
import Reveal from '../components/Reveal';
import TitreSection from '../components/TitreSection';
import AppelAction from '../components/AppelAction';
import CarteMachine from '../components/CarteMachine';
import Pictogramme from '../components/Pictogramme';
import { MACHINES, ENTREPRISE } from '../data/site';

export default function FicheMachine() {
  const { slug } = useParams();
  const m = MACHINES.find((x) => x.slug === slug);
  if (!m) return <Navigate to="/nos-engins" replace />;
  const autres = MACHINES.filter((x) => x.slug !== slug).slice(0, 3);

  // Les noms generiques (chariot elevateur, nacelle, compresseur) n'ont ni le meme
  // genre ni la meme casse qu'un nom de modele au milieu d'une phrase.
  const leNom = `${m.leLa} ${m.nomTexte}`;
  const duNom = m.leLa === 'le' ? `du ${m.nomTexte}` : `de la ${m.nomTexte}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: m.nom,
    description: m.type + '. ' + m.accroche,
    ...(m.image ? { image: ENTREPRISE.site + m.image } : {}),
    brand: { '@type': 'Brand', name: m.nom.split(' ')[0] },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'EUR',
      seller: { '@type': 'Organization', name: ENTREPRISE.nom },
      url: ENTREPRISE.site + '/nos-engins/' + m.slug,
    },
  };

  return (
    <>
      <Seo
        titre={`Location ${m.nom} | ${m.type} | Île-de-France`}
        description={`${m.nom} : ${m.type.toLowerCase()} en location avec ou sans chauffeur à Alfortville (94). ${m.accroche}`}
        jsonLd={jsonLd}
      />

      <section className="bg-nuit text-white relative overflow-hidden">
        <div className="absolute inset-0 trame-diagonale" aria-hidden="true" />
        <div className="conteneur relative py-12 sm:py-14">
          <nav className="font-cond uppercase tracking-[0.09em] text-[13px] text-white/50 mb-6" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-jaune">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/nos-engins" className="hover:text-jaune">Nos engins</Link>
            <span className="mx-2">/</span>
            <span className="text-white/80">{m.nom}</span>
          </nav>

          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-center">
            <div>
              <h1 className="font-display uppercase text-[34px] sm:text-[46px] leading-[1]">
                Location <span className="mot-degrade">{m.nom}</span>
              </h1>
              <p className="mt-3 font-cond uppercase font-semibold tracking-[0.06em] text-[17px] text-jaune">
                {m.type}
              </p>
              <p className="mt-5 text-[18px] text-white/75 max-w-xl">{m.accroche}</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="btn-jaune">Demander mon devis</Link>
                <a href={ENTREPRISE.telephoneLien} className="btn-contour">{ENTREPRISE.telephone}</a>
              </div>
            </div>
            <div className="relative flex items-end justify-center pb-10 lg:pb-12">
              <span
                className="absolute bottom-7 left-1/2 -translate-x-1/2 w-[78%] h-[26px] rounded-[50%] bg-black/60 blur-[14px]"
                aria-hidden="true"
              />
              <span className="absolute -inset-x-2 bottom-0 h-2 bande-chantier" aria-hidden="true" />
              {m.visuel || m.image ? (
                <img
                  src={m.visuel || m.image}
                  alt={`${m.nom}, ${m.type.toLowerCase()}, disponible à la location chez BATI SELECT TP`}
                  className="relative object-contain max-h-[300px] sm:max-h-[400px] lg:max-h-[460px] w-auto mx-auto"
                  style={m.visuelHauteurMax ? { maxHeight: m.visuelHauteurMax } : undefined}
                />
              ) : (
                <Pictogramme
                  nom={m.pictogramme}
                  classe="relative w-full max-w-[420px] opacity-80"
                />
              )}
            </div>
          </div>
        </div>
        <Bande hauteur={6} />
      </section>

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14">
          <div>
            <TitreSection avant="Ce qu'on apporte de " mot="concret" />
            <p className="mt-5 text-nuit/85 text-[17px]">{m.accroche}</p>
            <ul className="mt-6 space-y-3">
              {[
                `Livraison ${duNom} sur votre chantier sous 24 à 48 h, partout en Île-de-France`,
                m.famille === 'energie'
                  ? 'Matériel révisé et assuré, livré avec sa notice'
                  : 'Machine révisée et assurée, avec son rapport de vérification et sa notice',
                'Location à la journée, à la semaine ou au mois',
                'Avec ou sans chauffeur, selon vos équipes et vos habilitations',
                "Intervention dans la journée en cas de souci pendant la location",
                "Un conseil direct sur la machine adaptée à votre accès et à votre terrain",
              ].map((x) => (
                <li key={x} className="flex gap-3">
                  <span className="mt-2 shrink-0 w-2.5 h-2.5 bg-jaune" aria-hidden="true" />
                  <span className="text-nuit/85">{x}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="self-start">
            <h2 className="font-cond uppercase font-bold tracking-[0.05em] text-[20px] text-nuit">
              Ce qu'elle fait bien
            </h2>
            <ul className="mt-5 space-y-3">
              {m.pour.map((p) => (
                <li key={p} className="flex gap-3 items-start">
                  <span className="mt-2 shrink-0 w-2.5 h-2.5 bg-bleu" aria-hidden="true" />
                  <span className="text-nuit/85">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 carte p-6 border-l-4 border-l-bleu">
              <h3 className="font-cond uppercase font-bold tracking-[0.04em] text-[17px] text-nuit">
                Zone d'intervention
              </h3>
              <p className="mt-2.5 text-nuit/75 text-[16px]">
                Au départ de notre dépôt d'Alfortville, nous livrons dans tout le Val-de-Marne,
                à Paris et dans l'ensemble de l'Île-de-France. Donnez-nous la commune de votre
                chantier, on vous confirme le délai.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AppelAction
        titre={`Besoin ${duNom} ?`}
        texte="Dites-nous vos dates et la commune du chantier, on vous confirme la disponibilité et le tarif en quelques heures."
      />

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14">
          <div>
            {m.specs ? (
              <>
            <TitreSection avant="Les chiffres " mot="exacts" />
            <table className="mt-7 w-full text-[16px]">
              <tbody>
                {m.specs.map(([k, v]) => (
                  <tr key={k} className="border-b border-nuit/10">
                    <th scope="row" className="text-left font-normal text-beton py-3 pr-6 align-top w-1/2">{k}</th>
                    <td className="py-3 font-semibold text-nuit">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-[14.5px] text-beton">
              Source : {m.source} Les valeurs peuvent varier selon la configuration exacte de la machine.
              La masse portée sur la plaque signalétique fait foi.
            </p>
              </>
            ) : (
              <>
                <TitreSection avant="Ce qu'il faut nous " mot="dire" />
                <p className="mt-5 text-nuit/85 text-[17px]">
                  Nous avons plusieurs configurations. Pour vous proposer la bonne du premier coup,
                  ces quatre informations suffisent.
                </p>
                <ul className="mt-6 space-y-3">
                  {m.aDire.map((x) => (
                    <li key={x} className="flex gap-3">
                      <span className="mt-2 shrink-0 w-2.5 h-2.5 bg-jaune" aria-hidden="true" />
                      <span className="text-nuit/85">{x}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 carte p-6 border-l-4 border-l-beton">
                  <h3 className="font-cond uppercase font-bold tracking-[0.04em] text-[17px] text-nuit">
                    Le modèle exact
                  </h3>
                  <p className="mt-2.5 text-nuit/75 text-[16px]">
                    Nous préférons ne pas publier de caractéristiques approximatives.
                    La marque, le modèle et les capacités précises de la machine qui vous sera
                    livrée vous sont confirmés par écrit sur le devis, avant toute réservation.
                  </p>
                </div>
              </>
            )}
          </div>

          <div>
            <div className="carte p-6 border-l-4 border-l-bleu">
              <h3 className="font-cond uppercase font-bold tracking-[0.04em] text-[17px] text-nuit">
                {m.famille === 'energie' ? 'Conduite et sécurité' : 'Avec ou sans chauffeur'}
              </h3>
              <p className="mt-2.5 text-nuit/75 text-[16px]">
                {m.famille === 'energie'
                  ? m.caces
                  : "Sans chauffeur si vos équipes ont le CACES qui convient et l'autorisation de conduite. Avec chauffeur si vous préférez que la machine arrive avec quelqu'un qui la connaît. Vous choisissez au moment du devis."}
              </p>
              {m.caces && m.famille !== 'energie' && (
                <p className="mt-2.5 text-beton text-[15px]">{m.caces}</p>
              )}
            </div>

            <div className="mt-5 carte p-6 border-l-4 border-l-jaune">
              <h3 className="font-cond uppercase font-bold tracking-[0.04em] text-[17px] text-nuit">
                Livraison sur votre chantier
              </h3>
              <p className="mt-2.5 text-nuit/75 text-[16px]">
                Sous 24 à 48 h partout en Île-de-France
                {m.famille === 'energie'
                  ? ', avec sa notice. On décharge sur place et on vient récupérer le matériel en fin de location.'
                  : ", avec son rapport de vérification à jour et sa notice. On décharge sur place et on vient récupérer la machine en fin de location."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {m.galerie?.length > 0 && (
        <section className="bg-trame py-14 sm:py-16">
          <div className="conteneur">
            <TitreSection avant={`${leNom.charAt(0).toUpperCase() + leNom.slice(1)} chez `} mot="nous" />
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {m.galerie.map((g, i) => (
                <Reveal key={g} delai={i * 0.06}>
                  <figure className="bg-white border border-nuit/10 p-3 h-full flex items-center justify-center">
                    <img src={g} alt={`${m.nom}, vue ${i + 2}`} loading="lazy" className="photo-entiere max-h-[300px] w-auto" />
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <AppelAction
        titre={`${leNom.charAt(0).toUpperCase() + leNom.slice(1)} vous intéresse ?`}
        texte="Dites-nous vos dates et la commune du chantier, on vous confirme la disponibilité et le tarif en quelques heures."
      />

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur">
          <TitreSection avant="Nos autres " mot="machines" />
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {autres.map((a) => <CarteMachine key={a.slug} machine={a} />)}
          </div>
        </div>
      </section>

      <SectionContact
        avant={`Réserver ${leNom}, ou en parler `}
        mot="d'abord"
        intro="Dites-nous vos dates, la commune du chantier et la largeur de votre accès. On confirme la disponibilité et le tarif en quelques heures."
      />
    </>
  );
}
